// frontend/src/components/CourseDetail.js
import React, { useContext, useState, useEffect } from 'react';
import { AuthContext } from './AuthContext';
import QrPaymentModal from './QrPaymentModal';
import { getCourseDetails } from '../api';
import { formatBobCurrency } from '../utilities/currency';
import { resolveMediaUrl } from '../utilities/media';

const CourseDetail = ({ courseId, onNavigate }) => {
  const { currentUser, isAuthenticated } = useContext(AuthContext);
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const response = await getCourseDetails(courseId);
        if (response.success) {
          setCourse(response.data);
        } else {
          setError(response.message || 'Error al cargar el curso');
        }
      } catch (err) {
        setError(err.message || 'Error de conexión');
      } finally {
        setLoading(false);
      }
    };

    fetchCourse();
  }, [courseId]);

  if (loading) return <div className="container mx-auto px-4 py-8">Cargando detalles del curso...</div>;
  if (error) return <div className="container mx-auto px-4 py-8 text-red-500">Error: {error}</div>;
  if (!course) return <div className="container mx-auto px-4 py-8 text-red-500">Curso no encontrado.</div>;

  const isEnrolled = currentUser?.enrolled?.includes(course.id);

  const handleEnrollClick = () => {
    if (!isAuthenticated) {
      alert('Debes iniciar sesión para inscribirte en un curso.');
      onNavigate('login');
      return;
    }
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = () => {
    setShowPaymentModal(false);
  };

  // Calcular duración total del curso
  const totalDuration = course.sections?.reduce((total, section) => {
    return total + (section.lectures?.reduce((secTotal, lecture) => {
      return secTotal + (lecture.duration_seconds || 0);
    }, 0) || 0);
  }, 0) || 0;

  // Formatear duración a horas:minutos
  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours > 0 ? `${hours}h ` : ''}${minutes}m`;
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col lg:flex-row gap-8">
        <div className="lg:w-2/3">
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">{course.title}</h1>
            <p className="text-xl text-gray-700 mb-4">{course.subtitle}</p>
            <div className="flex items-center space-x-4 text-sm text-gray-600">
              <span>Nivel: {course.level}</span>
              <span>Duración: {formatDuration(totalDuration)}</span>
              <span>Idioma: {course.language}</span>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h2 className="text-xl font-bold mb-4">Descripción del curso</h2>
            <p className="text-gray-700">{course.description}</p>
          </div>

          {course.goals?.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <h2 className="text-xl font-bold mb-4">Lo que aprenderás</h2>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {course.goals.map((goal, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    <span>{goal.goal_text}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {course.requirements?.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <h2 className="text-xl font-bold mb-4">Requisitos</h2>
              <ul className="space-y-2">
                {course.requirements.map((req, index) => (
                  <li key={index} className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>{req.requirement_text}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {course.sections?.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <h2 className="text-xl font-bold mb-4">Contenido del Curso</h2>
              <div className="space-y-4">
                {course.sections.map(section => (
                  <div key={section.id} className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-gray-50 p-4 border-b">
                      <h3 className="font-semibold">{section.title}</h3>
                    </div>
                    <ul className="divide-y divide-gray-200">
                      {section.lectures?.map(lecture => (
                        <li key={lecture.id} className="p-4 hover:bg-gray-50 flex justify-between items-center">
                          <div className="flex items-center">
                            <span className="mr-4 text-gray-500">{lecture.position}.</span>
                            <span>{lecture.title}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            {lecture.is_preview && (
                              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                Vista previa
                              </span>
                            )}
                            <span className="text-sm text-gray-500">
                              {formatDuration(lecture.duration_seconds || 0)}
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="lg:w-1/3">
          <div className="bg-white rounded-lg shadow-sm p-6 sticky top-4">
            <img
              src={resolveMediaUrl(course.thumbnail_url, 'default-thumbnail.jpg')}
              alt={course.title}
              className="w-full mb-4 rounded-lg"
            />
            <div className="flex justify-between items-center mb-4">
              <span className="text-2xl font-bold">{formatBobCurrency(course.price)}</span>
            </div>
            
            {isEnrolled ? (
              <button
                className="w-full py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 mb-2 transition-colors"
                onClick={() => onNavigate('learnCourse', course.id)}
              >
                Ir al curso
              </button>
            ) : (
              <button
                className="w-full py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 mb-2 transition-colors"
                onClick={handleEnrollClick}
              >
                Inscribirse ahora
              </button>
            )}
            
            <div className="text-sm text-gray-600 space-y-2 mt-4">
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Duración: {formatDuration(totalDuration)}</span>
              </div>
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                </svg>
                <span>Nivel: {course.level}</span>
              </div>
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                </svg>
                <span>Idioma: {course.language}</span>
              </div>
            </div>
            
            <p className="text-center text-sm text-gray-500 mt-4">Garantía de devolución de 30 días</p>
          </div>
          
          {course.instructor && (
            <div className="bg-white rounded-lg shadow-sm p-6 mt-6">
              <h3 className="font-bold mb-4">Instructor</h3>
              <div className="flex items-center">
                <img 
                  src={resolveMediaUrl(course.instructor.profile_picture, 'default-instructor.jpg')} 
                  alt={course.instructor.name}
                  className="w-12 h-12 rounded-full mr-4"
                />
                <div>
                  <h4 className="font-medium">{course.instructor.name}</h4>
                  <p className="text-sm text-gray-600">{course.instructor.bio || 'Instructor certificado'}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showPaymentModal && (
        <QrPaymentModal
          course={course}
          onClose={() => setShowPaymentModal(false)}
          onPaymentSuccess={handlePaymentSuccess}
        />
      )}
    </div>
  );
};

export default CourseDetail;
